import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Package, ArrowRight, Star, TrendingUp, ChevronLeft, ChevronRight, Rocket, BadgeCheck, Heart, Clock, Shield, Zap, Users, MapPin, Sparkles, Eye, Gift, ThumbsUp, Award, Globe, CheckCircle } from 'lucide-react';

const HeroOptionsPage = () => {
  const [currentOption, setCurrentOption] = useState(0);
  
  const options = [
    { id: 'A', name: 'Social Proof Focus', description: 'Stats prominently displayed' },
    { id: 'B', name: 'Search-Centric', description: 'Big search bar as main focus' },
    { id: 'C', name: 'Minimalist + Trust', description: 'Clean with trust badges' },
    { id: 'D', name: 'Urgency/FOMO', description: 'Live viewers & animations' },
    { id: 'E', name: 'Category Quick Access', description: 'Popular categories in hero' },
    { id: 'F', name: 'Testimonial Focus', description: 'User quotes & ratings' },
    { id: 'G', name: 'Video/Visual Heavy', description: 'Featured listings preview' },
    { id: 'H', name: 'Island Pride', description: 'ABC flags & local feel' },
  ];

  const nextOption = () => setCurrentOption((prev) => (prev + 1) % options.length);
  const prevOption = () => setCurrentOption((prev) => (prev - 1 + options.length) % options.length);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-800/95 backdrop-blur-md border-b border-slate-700">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-white font-bold flex items-center gap-2">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Site
          </Link>
          <h1 className="text-white font-bold">Hero Layout Options</h1>
          <div className="flex items-center gap-2">
            <button onClick={prevOption} className="p-2 bg-slate-700 rounded-lg text-white hover:bg-slate-600">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white font-medium px-3">
              Option {options[currentOption].id} of {options.length}
            </span>
            <button onClick={nextOption} className="p-2 bg-slate-700 rounded-lg text-white hover:bg-slate-600">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Option selector pills */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-slate-800/80 backdrop-blur-sm border-b border-slate-700 py-2">
        <div className="container mx-auto px-4 flex gap-2 overflow-x-auto scrollbar-hide">
          {options.map((opt, idx) => (
            <button
              key={opt.id}
              onClick={() => setCurrentOption(idx)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                currentOption === idx 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {opt.id}: {opt.name}
            </button>
          ))}
        </div>
      </div>

      {/* Hero Preview Area */}
      <div className="pt-32">
        {/* Option A: Social Proof Focus */}
        {currentOption === 0 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            <div className="relative z-10 container mx-auto px-4 py-16 text-center">
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-16 lg:h-20 mx-auto mb-4" />
              <p className="text-2xl lg:text-3xl font-bold text-white mb-2">The Caribbean's #1 Marketplace</p>
              <p className="text-lg text-white/70 mb-8">Buy, sell & trade across Aruba, Bonaire & Curaçao</p>
              
              <div className="flex justify-center gap-8 mb-10">
                <div className="text-center">
                  <div className="text-4xl font-black text-primary">2,500+</div>
                  <div className="text-white/60 text-sm">Active Listings</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-emerald-400">5,000+</div>
                  <div className="text-white/60 text-sm">Happy Users</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-amber-400">100%</div>
                  <div className="text-white/60 text-sm">Free Forever</div>
                </div>
              </div>
              
              <div className="flex justify-center gap-4">
                <button className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-10 py-4 rounded-xl font-bold text-lg shadow-xl">
                  <Package className="w-5 h-5" /> Sell Now Free <ArrowRight className="w-5 h-5" />
                </button>
                <button className="inline-flex items-center gap-2 bg-white hover:bg-white/90 text-slate-900 px-10 py-4 rounded-xl font-bold text-lg">
                  <Search className="w-5 h-5" /> Browse Listings
                </button>
              </div>
              
              <div className="mt-8">
                <span className="text-pink-400 text-xl font-bold">Powered by:</span>
                <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-28 mx-auto mt-2" />
              </div>
            </div>
          </section>
        )}

        {/* Option B: Search-Centric */}
        {currentOption === 1 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            <div className="relative z-10 container mx-auto px-4 py-16 text-center">
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-14 lg:h-16 mx-auto mb-3" />
              <h1 className="text-4xl lg:text-5xl font-black text-white mb-3">Find Your Next Deal</h1>
              <p className="text-lg text-white/70 mb-8">Thousands of items for sale across the ABC Islands</p>
              
              <div className="max-w-2xl mx-auto mb-8">
                <div className="flex bg-white rounded-2xl p-2 shadow-2xl">
                  <input type="text" placeholder="Search cars, electronics, real estate..." className="flex-1 px-4 py-3 text-lg text-slate-800 bg-transparent outline-none" />
                  <button className="px-8 py-3 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl">Search</button>
                </div>
              </div>
              
              <div className="flex justify-center gap-4 mb-8">
                <span className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white/90 text-sm font-medium border border-white/20">✓ 100% Free</span>
                <span className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white/90 text-sm font-medium border border-white/20">✓ No Commission</span>
                <span className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white/90 text-sm font-medium border border-white/20">✓ Instant Posting</span>
              </div>
              
              <button className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl">
                <Package className="w-5 h-5" /> Start Selling — It's Free!
              </button>
              
              <div className="mt-8">
                <span className="text-pink-400 text-xl font-bold">Powered by:</span>
                <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-28 mx-auto mt-2" />
              </div>
            </div>
          </section>
        )}

        {/* Option C: Minimalist + Trust */}
        {currentOption === 2 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            <div className="relative z-10 container mx-auto px-4 py-16 text-center">
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-20 lg:h-24 mx-auto mb-6" />
              <p className="text-xl lg:text-2xl text-white/80 mb-10 max-w-xl mx-auto">The trusted marketplace for Aruba, Bonaire & Curaçao</p>
              
              <div className="flex flex-col items-center gap-4 mb-10">
                <button className="inline-flex items-center gap-3 bg-gradient-to-r from-primary to-blue-500 text-white px-12 py-5 rounded-2xl font-bold text-xl shadow-2xl shadow-primary/30">
                  <Package className="w-6 h-6" /> Post Your Ad — Free Forever
                </button>
                <span className="inline-flex items-center gap-2 text-white/80 font-medium">
                  <Search className="w-5 h-5" /> Or browse 2,500+ listings <ArrowRight className="w-4 h-4" />
                </span>
              </div>
              
              <div className="flex justify-center items-center gap-6 mb-8">
                <div className="flex items-center gap-2 text-white/70">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <BadgeCheck className="w-4 h-4 text-emerald-400" />
                  </div>
                  <span className="text-sm">Verified Sellers</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <Star className="w-4 h-4 text-amber-400" />
                  </div>
                  <span className="text-sm">5,000+ Users</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Shield className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-sm">Safe & Secure</span>
                </div>
              </div>
              
              <div className="mt-6">
                <span className="text-pink-400 text-xl font-bold">Powered by:</span>
                <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-28 mx-auto mt-2" />
              </div>
            </div>
          </section>
        )}

        {/* Option D: Urgency/FOMO */}
        {currentOption === 3 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            <div className="relative z-10 container mx-auto px-4 py-12 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-full mb-4">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <span className="text-red-400 text-sm font-semibold">47 people viewing now</span>
              </div>
              
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-16 lg:h-20 mx-auto mb-4" />
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">🏝️ ABC Islands' Favorite Marketplace</h1>
              <p className="text-lg text-white/70 mb-6">Join 5,000+ islanders buying & selling daily</p>
              
              <div className="flex justify-center gap-4 mb-6">
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-md rounded-2xl px-5 py-4 border border-emerald-500/30">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-emerald-400" />
                    <span className="text-2xl font-black text-emerald-400">FREE</span>
                  </div>
                  <div className="text-white/60 text-xs">Forever, no hidden fees</div>
                </div>
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur-md rounded-2xl px-5 py-4 border border-amber-500/30">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-400" />
                    <span className="text-2xl font-black text-amber-400">2,547</span>
                  </div>
                  <div className="text-white/60 text-xs">Active listings</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-md rounded-2xl px-5 py-4 border border-blue-500/30">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <span className="text-2xl font-black text-blue-400">60s</span>
                  </div>
                  <div className="text-white/60 text-xs">To post your first ad</div>
                </div>
              </div>
              
              <div className="flex justify-center gap-4">
                <button className="inline-flex items-center gap-2 bg-gradient-to-r from-primary via-blue-500 to-cyan-500 text-white px-10 py-4 rounded-xl font-bold text-lg shadow-xl animate-pulse">
                  <Rocket className="w-5 h-5" /> Start Selling Now
                </button>
                <button className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-xl font-bold text-lg border border-white/20">
                  <Search className="w-5 h-5" /> Explore Deals
                </button>
              </div>
              
              <div className="mt-6">
                <span className="text-pink-400 text-xl font-bold">Powered by:</span>
                <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-24 mx-auto mt-2" />
              </div>
            </div>
          </section>
        )}

        {/* Option E: Category Quick Access */}
        {currentOption === 4 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            <div className="relative z-10 container mx-auto px-4 py-12 text-center">
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-14 mx-auto mb-3" />
              <p className="text-xl text-white/80 mb-6">What are you looking for today?</p>
              
              <div className="grid grid-cols-4 gap-3 max-w-2xl mx-auto mb-8">
                {[
                  { icon: '🚗', name: 'Vehicles', count: '450+' },
                  { icon: '🏠', name: 'Real Estate', count: '320+' },
                  { icon: '📱', name: 'Electronics', count: '680+' },
                  { icon: '👔', name: 'Fashion', count: '290+' },
                  { icon: '🛋️', name: 'Furniture', count: '180+' },
                  { icon: '💼', name: 'Jobs', count: '95+' },
                  { icon: '🔧', name: 'Services', count: '210+' },
                  { icon: '✨', name: 'View All', count: '2,500+' },
                ].map((cat) => (
                  <button key={cat.name} className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all group">
                    <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">{cat.icon}</span>
                    <span className="text-white font-medium text-sm block">{cat.name}</span>
                    <span className="text-white/50 text-xs">{cat.count}</span>
                  </button>
                ))}
              </div>
              
              <div className="flex justify-center gap-4 mb-6">
                <button className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-bold shadow-xl">
                  <Package className="w-5 h-5" /> Sell Something
                </button>
                <button className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-xl font-medium border border-white/20">
                  100% Free • No Fees
                </button>
              </div>
              
              <div className="mt-4">
                <span className="text-pink-400 text-lg font-bold">Powered by:</span>
                <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-20 mx-auto mt-2" />
              </div>
            </div>
          </section>
        )}

        {/* Option F: Testimonial Focus */}
        {currentOption === 5 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            <div className="relative z-10 container mx-auto px-4 py-12 text-center">
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-16 mx-auto mb-4" />
              
              <div className="flex justify-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 text-amber-400 fill-amber-400" />)}
              </div>
              <p className="text-white/70 text-sm mb-6">Rated 4.9/5 by 2,000+ users</p>
              
              <div className="max-w-xl mx-auto mb-8">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                  <p className="text-white text-lg italic mb-4">"Sold my car within 2 days! Best marketplace on the islands. Easy to use and completely free!"</p>
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-blue-500 rounded-full flex items-center justify-center text-white font-bold">M</div>
                    <div className="text-left">
                      <p className="text-white font-medium">Maria S.</p>
                      <p className="text-white/50 text-sm">Curaçao • Verified Seller</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center gap-4 mb-6">
                <button className="inline-flex items-center gap-2 bg-primary text-white px-10 py-4 rounded-xl font-bold text-lg shadow-xl">
                  <Package className="w-5 h-5" /> Start Selling Free
                </button>
                <button className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-lg">
                  <Search className="w-5 h-5" /> Browse Deals
                </button>
              </div>
              
              <div className="flex justify-center gap-6 text-white/60 text-sm mb-6">
                <span className="flex items-center gap-1"><ThumbsUp className="w-4 h-4" /> 5,000+ Happy Users</span>
                <span className="flex items-center gap-1"><Gift className="w-4 h-4" /> 100% Free</span>
                <span className="flex items-center gap-1"><Award className="w-4 h-4" /> #1 on ABC Islands</span>
              </div>
              
              <div className="mt-4">
                <span className="text-pink-400 text-lg font-bold">Powered by:</span>
                <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-24 mx-auto mt-2" />
              </div>
            </div>
          </section>
        )}

        {/* Option G: Featured Listings Preview */}
        {currentOption === 6 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50" />
            <div className="relative z-10 container mx-auto px-4 py-10">
              <div className="grid grid-cols-2 gap-8 items-center">
                <div className="text-left">
                  <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-14 mb-4" />
                  <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">Discover Amazing Deals on the Islands</h1>
                  <p className="text-lg text-white/70 mb-6">2,500+ items waiting for you</p>
                  
                  <div className="flex gap-3 mb-6">
                    <button className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-bold shadow-xl">
                      <Search className="w-5 h-5" /> Browse Now
                    </button>
                    <button className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-4 rounded-xl font-bold border border-white/20">
                      <Package className="w-5 h-5" /> Sell Free
                    </button>
                  </div>
                  
                  <div className="mt-4">
                    <span className="text-pink-400 font-bold">Powered by:</span>
                    <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-20 mt-2" />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { title: 'Toyota Hilux 2022', price: '$28,500', img: 'https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=300&h=200&fit=crop' },
                    { title: 'Beachfront Apartment', price: '$1,200/mo', img: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=300&h=200&fit=crop' },
                    { title: 'iPhone 15 Pro', price: '$899', img: 'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=300&h=200&fit=crop' },
                    { title: 'Vintage Rolex', price: '$4,500', img: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=200&fit=crop' },
                  ].map((item, i) => (
                    <div key={i} className="bg-white/10 backdrop-blur-md rounded-xl overflow-hidden border border-white/20 hover:border-white/40 transition-all group cursor-pointer">
                      <img src={item.img} alt={item.title} className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                      <div className="p-3">
                        <p className="text-white text-sm font-medium truncate">{item.title}</p>
                        <p className="text-primary font-bold">{item.price}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Option H: Island Pride */}
        {currentOption === 7 && (
          <section className="relative min-h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/uqygu1ko_PHOTO-2026-03-18-14-04-55%202.jpg')` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            <div className="relative z-10 container mx-auto px-4 py-12 text-center">
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="YBS" className="h-16 lg:h-20 mx-auto mb-4" />
              
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                Made for <span className="text-primary">Our Islands</span>
              </h1>
              <p className="text-lg text-white/70 mb-8">The only marketplace built by islanders, for islanders</p>
              
              <div className="flex justify-center gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-md rounded-xl px-6 py-4 border border-white/20">
                  <Globe className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="text-white font-bold">3 Islands</p>
                  <p className="text-white/50 text-xs">Connected</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-xl px-6 py-4 border border-white/20">
                  <Clock className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-white font-bold">60 Sec</p>
                  <p className="text-white/50 text-xs">Create & List</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-xl px-6 py-4 border border-white/20">
                  <CheckCircle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-white font-bold">100%</p>
                  <p className="text-white/50 text-xs">Free Forever</p>
                </div>
              </div>
              
              <div className="flex justify-center gap-4 mb-6">
                <button className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-500 text-white px-10 py-4 rounded-xl font-bold text-lg shadow-xl">
                  <Rocket className="w-5 h-5" /> Start Now!
                </button>
                <button className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-lg">
                  <Search className="w-5 h-5" /> Browse Listings
                </button>
              </div>
              
              <div className="mt-6">
                <span className="text-pink-400 text-xl font-bold">Powered by:</span>
                <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" alt="Paradise FM" className="h-24 mx-auto mt-2" />
              </div>
            </div>
          </section>
        )}

        {/* Description Box */}
        <div className="bg-slate-800 border-t border-slate-700 p-6">
          <div className="container mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">Option {options[currentOption].id}: {options[currentOption].name}</h2>
                <p className="text-slate-400">{options[currentOption].description}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-400 text-sm">Use arrow keys or buttons to navigate</p>
                <p className="text-primary font-medium">← Previous | Next →</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroOptionsPage;
